import React, { useCallback, useEffect } from 'react';
import { FeedbackQuestionHandler, FeedbackSectionInfo } from '../utils';
import SectionIndicatorDetails from '../../../ReportBuilder/Components/IndicatorDetails/SectionIndicatorDetails';
import { SectionIndicators } from '../../../ReportBuilder/Components/IndicatorDetails/utils';
import { isCapacityDevelopmentEnabled } from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import { DATES_QUESTION_ID_1 } from '../../../ReportBuilder/Components/ReportFinalize/answersUtil';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
} from '../../utils';
import { FeedbackUpdateAnswerResponse, updateAnswer } from '../api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { removeConnections, useClient } from 'shared/apollo';
import { parseImpactDetailsAnswer } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails';
import {
  parseReportPeriod,
  ReportPeriod,
} from '../../../ReportBuilder/Components/SectionPeriod/utils';
import ImpactModel from './ImpactModel';
import {
  Answer,
  getAnswersBySectionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';

const ImpactDetails = React.forwardRef<
  FeedbackQuestionHandler,
  FeedbackSectionInfo
>(
  (
    {
      reportId,
      questionId,
      feedback,
      reportStage,
      reportStatus,
      question,
      questions,
      programName,
      questionText,
      answer,
      isReportPassed,
      handleCompleted,
      handleClose,
      reloadAnswers,
      setSelectedQuestion,
    },
    forwardRef,
  ) => {
    const client = useClient();
    const [requestAnswerState, makeAnswerRequest] = useGraphQLRequest<{
      [id: number]: Answer;
    }>();
    const [requestState, makeRequest] = useGraphQLRequest<
      FeedbackUpdateAnswerResponse
    >();

    const handleRefresh = useCallback(() => {
      if (!client) return;
      makeAnswerRequest(
        getAnswersBySectionId(client, reportId).then(r => {
          const result: { [id: number]: Answer } = {};
          const answers: Answer[] = removeConnections(
            r.data.allNpoReportAnswers,
          );
          for (const a of answers) {
            result[a.question.dbId] = a;
          }
          return {
            data: result,
          };
        }),
      );
    }, [client, makeAnswerRequest]);

    useEffect(() => {
      handleRefresh();
    }, [handleRefresh]);

    useEffect(() => {
      if (requestState.type !== 'REQUEST_SUCCESS') return;
      setSelectedQuestion(s => {
        if (!s) return s;
        if (s.dbId === INDICATOR_DETAILS_QUESTION_ID) {
          return questions[INDICATOR_SUMMARY_QUESTION_ID];
        }
        return undefined;
      });
    }, [requestState]);

    const handleSaveAnswer = useCallback(
      (questionId: number, answer: string) => {
        if (!client) return;
        makeRequest(
          updateAnswer(client!, {
            // id,
            reportId,
            answerId: undefined,
            questionId,
            ignoreClearData: true,
            answer,
          }),
        );
      },
      [],
    );

    const answers = requestAnswerState.data;

    if (!answers) {
      return <TrueImpactLoading />;
    }

    const serviceType = answers[40006]?.text;
    const serviceGoal = answers[40003]?.text;

    const intermediaries = answers[40022]?.text;
    const reportDates = answers[DATES_QUESTION_ID_1]?.text;
    const beneficiaryTerm = answers[40004]?.text;

    const learnEnabled = answers[40007]?.text === 'Yes';
    const actEnabled = answers[40008]?.text === 'Yes';
    const capacityDevelopmentIndicators = answers[40021]?.text;
    const capacityDevelopmentEnabled = isCapacityDevelopmentEnabled(
      capacityDevelopmentIndicators,
    );

    if (questionId === INDICATOR_SUMMARY_QUESTION_ID) {
      const reportPeriod: ReportPeriod = parseReportPeriod(reportDates);
      const defaultAnswer: SectionIndicators = {
        act: [],
        capacityDevelopment: [],
        learn: [],
        catalyzed: [],
        reach: [],
        succeed: [],
      };
      const impactDetailAnswer = answers[INDICATOR_DETAILS_QUESTION_ID];
      const answer =
        parseImpactDetailsAnswer(impactDetailAnswer?.text) || defaultAnswer;
      return (
        <ImpactModel
          reportId={reportId}
          feedback={feedback}
          reportStage={reportStage}
          reportStatus={reportStatus}
          programName={programName}
          isReportPassed={isReportPassed}
          selectedIndicators={answer}
          serviceType={serviceType}
          serviceGoal={serviceGoal}
          intermediaries={intermediaries}
          reportPeriod={reportPeriod}
          requestState={requestAnswerState}
          beneficiaryTerm={beneficiaryTerm}
          handleClose={handleClose}
          handleDone={handleCompleted}
          reloadAnswers={reloadAnswers}
          impactDetailAnswerId={impactDetailAnswer?.dbId || undefined}
          onNextSection={() => setSelectedQuestion(undefined)}
          handleSaveAnswer={handleSaveAnswer}
          handleBack={() =>
            setSelectedQuestion(questions[INDICATOR_DETAILS_QUESTION_ID])
          }
        />
      );
    }
    return (
      <SectionIndicatorDetails
        reportId={reportId}
        allIndicators={{}}
        saving={requestState.type === 'REQUEST_START'}
        beneficiaryTerm={beneficiaryTerm}
        reportDates={reportDates}
        intermediaries={intermediaries}
        serviceGoal={serviceGoal}
        serviceType={serviceType}
        value={answer}
        capacityDevelopmentEnabled={capacityDevelopmentEnabled}
        learnEnabled={learnEnabled}
        actEnabled={actEnabled}
        isReportPassed={isReportPassed}
        programName={programName}
        reloadAnswers={() => null}
        onPreviousSection={() =>
          setSelectedQuestion(questions[IMPACT_SUMMARY_QUESTION_ID])
        }
        handleNext={(_: boolean, answer?: string) => {
          if (!answer) return;
          handleSaveAnswer(INDICATOR_DETAILS_QUESTION_ID, answer);
        }}
        goToDateSection={() => null}
        onNextSection={() =>
          setSelectedQuestion(questions[INDICATOR_SUMMARY_QUESTION_ID])
        }
        guidanceText={{}}
      />
    );
  },
);

export default ImpactDetails;
